import React from 'react';

interface SuccessModalDetail {
  label: string;
  value: string | number;
  type?: 'text' | 'email' | 'password' | 'id' | 'code' | 'amount';
  copyable?: boolean;
}

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  /** `warning` = red/white alert styling (e.g. blacklisted payout). `default` = dark slate + emerald (errors / generic). */
  variant?: 'default' | 'warning';
  details?: SuccessModalDetail[];
  warning?: {
    icon?: 'warning' | 'info' | 'success';
    text: string;
  };
  primaryAction?: {
    text: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'success';
  };
  showCloseButton?: boolean;
  autoClose?: number; // Auto close after X milliseconds
}

const SuccessModal: React.FC<SuccessModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'default',
  details = [],
  warning,
  primaryAction,
  showCloseButton = true,
  autoClose
}) => {
  // Auto close functionality
  React.useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoClose);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, onClose]);

  if (!isOpen) return null;

  const isWarning = variant === 'warning';

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log('Copied to clipboard:', text);
    });
  };

  const formatValue = (value: string | number, type?: string) => {
    if (typeof value === 'number') return value.toString();
    
    switch (type) {
      case 'amount':
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency: 'INR'
        }).format(parseFloat(value));
      case 'email':
        return value;
      case 'id':
        return `#${value}`;
      default:
        return value;
    }
  };

  const getValueClassName = (type?: string) => {
    switch (type) {
      case 'password':
      case 'code':
        return 'temp-password';
      case 'amount':
        return 'amount-value';
      case 'id':
        return 'id-value';
      default:
        return 'detail-value';
    }
  };

  const getWarningIcon = (iconType?: string) => {
    switch (iconType) {
      case 'info':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 16v-4M12 8h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default: // warning
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-5 backdrop-blur-sm" onClick={handleBackdropClick}>
      <div
        className={
          isWarning
            ? 'bg-white rounded-2xl shadow-none max-w-lg w-full min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden border border-red-200'
            : 'bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full min-w-0 max-h-[90vh] overflow-y-auto overflow-x-hidden border border-white/[0.08]'
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={
            isWarning
              ? 'flex items-center gap-4 p-6 border-b border-red-100 bg-red-50 min-w-0'
              : 'flex items-center gap-4 p-6 border-b border-white/[0.1] bg-slate-700/60 min-w-0'
          }
        >
          {isWarning ? (
            <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          ) : (
            <div className="w-12 h-12 rounded-full bg-emerald-500/90 flex items-center justify-center flex-shrink-0">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M8 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
          <h2 className={`text-xl font-bold m-0 flex-1 min-w-0 break-words ${isWarning ? 'text-red-800' : 'text-slate-100'}`}>{title}</h2>
          {showCloseButton && (
            <button
              type="button"
              className={
                isWarning
                  ? 'w-10 h-10 flex items-center justify-center rounded-lg border-none text-red-700/80 cursor-pointer hover:bg-red-100 transition-colors'
                  : 'w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-300 cursor-pointer hover:bg-white/10 transition-colors'
              }
              onClick={onClose}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
        <div className={`p-6 min-w-0 ${isWarning ? 'bg-white' : ''}`}>
          <p className={`m-0 mb-4 whitespace-pre-line break-words ${isWarning ? 'text-slate-800' : 'text-slate-300'}`}>{message}</p>
          {details.length > 0 && (
            <div className="space-y-3 mb-4 min-w-0">
              {details.map((detail, index) => (
                <div
                  key={index}
                  className={`flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 py-2 px-3 rounded-lg min-w-0 ${
                    isWarning
                      ? detail.type === 'password' || detail.type === 'code'
                        ? 'bg-red-50 border border-red-100'
                        : 'bg-slate-50 border border-slate-100'
                      : detail.type === 'password' || detail.type === 'code'
                        ? 'bg-slate-700/60'
                        : 'bg-slate-700/40'
                  }`}
                >
                  <span className={`text-sm font-semibold shrink-0 ${isWarning ? 'text-slate-600' : 'text-slate-400'}`}>{detail.label}:</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-sm font-medium break-all ${isWarning ? 'text-slate-900' : 'text-slate-200'}`}>{formatValue(detail.value, detail.type)}</span>
                    {(detail.copyable || detail.type === 'password' || detail.type === 'code') && (
                      <button
                        type="button"
                        className={
                          isWarning
                            ? 'p-1.5 rounded bg-red-100 text-red-800 cursor-pointer hover:bg-red-200 transition-colors'
                            : 'p-1.5 rounded bg-white/10 text-slate-300 cursor-pointer hover:bg-white/20 transition-colors'
                        }
                        onClick={() => copyToClipboard(detail.value.toString())}
                        title="Copy to clipboard"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2"/>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {warning && (
            <div
              className={
                isWarning
                  ? 'flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 mb-4'
                  : 'flex gap-3 p-4 rounded-xl bg-amber-900/30 border border-amber-600/40 mb-4'
              }
            >
              <div className={`flex-shrink-0 ${isWarning ? 'text-amber-600' : 'text-amber-400'}`}>{getWarningIcon(warning.icon)}</div>
              <p className={`text-sm m-0 ${isWarning ? 'text-amber-900' : 'text-amber-200'}`}>{warning.text}</p>
            </div>
          )}
        </div>
        <div className={`flex justify-center gap-2 p-6 ${isWarning ? 'border-t border-red-100 bg-white' : 'border-t border-white/[0.1]'}`}>
          {primaryAction ? (
            <button type="button" className="py-2.5 px-5 rounded-lg font-semibold text-sm bg-gradient-to-r from-blue-500 to-blue-600 text-white border-none cursor-pointer hover:opacity-90 hover:shadow-lg transition-all" onClick={primaryAction.onClick}>{primaryAction.text}</button>
          ) : (
            <button
              type="button"
              className={
                isWarning
                  ? 'flex items-center justify-center gap-2 py-2.5 px-5 rounded-lg font-semibold text-sm bg-red-600 text-white border border-red-700 cursor-pointer hover:opacity-95 transition-opacity'
                  : 'flex items-center justify-center gap-2 py-2.5 px-5 rounded-lg font-semibold text-sm bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-none cursor-pointer hover:opacity-90 hover:shadow-lg transition-all'
              }
              onClick={onClose}
            >
              {!isWarning && (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              Got it!
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;
